/**
 * Utility functions for handling query notes JSON structure
 *
 * The notes field stores JSON with the following structure:
 * {
 *   notes: "actual user notes text",
 *   dateWriting: "ISO date string or null",
 *   reportDate: "YYYY-MM-DD HH:mm:ss or null",
 *   draftCompany: "Company Name or null"
 * }
 */

/**
 * Parse query notes field from JSON string to object
 * @param {string} notesValue - The stringified JSON notes from the query
 * @returns {Object} Parsed notes object with notes, dateWriting, reportDate, and draftCompany
 */
export const parseQueryNotes = (notesValue) => {
  const defaultValue = { notes: '', dateWriting: null, reportDate: null, draftCompany: null };

  if (!notesValue) return defaultValue;

  try {
    const parsed = JSON.parse(notesValue);
    if (typeof parsed === 'object' && parsed !== null) {
      return {
        notes: parsed.notes || '',
        dateWriting: parsed.dateWriting || null,
        reportDate: parsed.reportDate || null,
        draftCompany: parsed.draftCompany || null,
      };
    } else {
      // If it's not JSON, treat as plain text notes
      return { ...defaultValue, notes: notesValue };
    }
  } catch (e) {
    // If JSON parsing fails, treat as plain text notes
    return { ...defaultValue, notes: notesValue };
  }
};

/**
 * Update a specific field in the query notes JSON
 * @param {string} existingNotesString - The current stringified JSON notes
 * @param {string} field - The field to update (notes, dateWriting, reportDate, or draftCompany)
 * @param {any} value - The new value for the field
 * @returns {string} Updated stringified JSON notes
 */
export const updateQueryNotesField = (existingNotesString, field, value) => {
  const currentNotes = parseQueryNotes(existingNotesString);

  // Update the specific field
  currentNotes[field] = value;

  // Return stringified JSON
  return JSON.stringify(currentNotes);
};

/**
 * Create query notes JSON string from individual fields
 * @param {Object} fields - Object containing notes, dateWriting, reportDate, and draftCompany
 * @returns {string} Stringified JSON notes
 */
export const createQueryNotesJson = ({ notes = '', dateWriting = null, reportDate = null, draftCompany = null }) => {
  return JSON.stringify({
    notes,
    dateWriting,
    reportDate,
    draftCompany,
  });
};

/**
 * Get the display date for a query (reportDate if set, otherwise fallback date)
 * @param {Object} query - The query object
 * @param {string} fallbackDate - The date to use if reportDate is not set (updateTime or date)
 * @returns {string} The date to display
 */
export const getQueryDisplayDate = (query, fallbackDate) => {
  const parsedNotes = parseQueryNotes(query.notes);
  return parsedNotes.reportDate || fallbackDate;
};

/**
 * Check if a query is using a custom report date
 * @param {Object} query - The query object
 * @returns {boolean} True if using custom reportDate
 */
export const hasCustomReportDate = (query) => {
  const parsedNotes = parseQueryNotes(query.notes);
  return Boolean(parsedNotes.reportDate);
};

/**
 * Get the draft company from query notes
 * @param {string} notesValue - The stringified JSON notes from the query
 * @returns {string|null} The draft company name or null
 */
export const getDraftCompany = (notesValue) => {
  const parsed = parseQueryNotes(notesValue);
  return parsed.draftCompany;
};
